import { z } from "zod";

import type {
  ServiceConfigurationValue,
  ServiceTemplateFieldDefinition,
} from "@louez/types";
import { evaluateCondition } from "@louez/utils";

export type ServiceQuoteFormValues = {
  name: string;
  email: string;
  phone: string;
  notes: string;
  configuration: Record<string, ServiceConfigurationValue>;
  enabledOptionalLineCodes: string[];
};

export const getServiceQuoteDefaults = (
  fields: ServiceTemplateFieldDefinition[],
): ServiceQuoteFormValues => ({
  name: "",
  email: "",
  phone: "",
  notes: "",
  configuration: Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.defaultValue ??
        (field.type === "number"
          ? 0
          : field.type === "checkbox"
            ? false
            : field.type === "select"
              ? (field.options[0]?.value ?? "")
              : ""),
    ]),
  ),
  enabledOptionalLineCodes: [],
});

export const getServiceQuoteSchema = (
  fields: ServiceTemplateFieldDefinition[],
) =>
  z
    .object({
      name: z.string().trim().min(2).max(255),
      email: z.email().max(255),
      phone: z.string().trim().max(50),
      notes: z.string().trim().max(2000),
      configuration: z.record(
        z.string(),
        z.union([z.number().finite(), z.string().max(2000), z.boolean()]),
      ),
      enabledOptionalLineCodes: z.array(z.string()).max(100),
    })
    .superRefine((values, context) => {
      for (const field of fields) {
        const value = values.configuration[field.key];
        if (!isServiceFieldVisible(field, values.configuration)) continue;
        if (
          field.required &&
          (value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === ""))
        ) {
          context.addIssue({
            code: "custom",
            message: field.label,
            path: ["configuration", field.key],
          });
        }
        if (
          field.type === "select" &&
          typeof value === "string" &&
          !field.options.some((option) => option.value === value)
        ) {
          context.addIssue({
            code: "custom",
            message: field.label,
            path: ["configuration", field.key],
          });
        }
      }
    });

export const isServiceFieldVisible = (
  field: ServiceTemplateFieldDefinition,
  configuration: Record<string, ServiceConfigurationValue>,
): boolean => {
  if (!field.visibilityCondition) return true;

  try {
    return evaluateCondition(
      field.visibilityCondition,
      configuration,
    );
  } catch {
    return false;
  }
};
